import * as Highcharts from "highcharts";
import 'highcharts/modules/heatmap';
import { getChartTheme } from './utils/chartTheme';
// v12: heatmap self-registers on side-effect import; adds Highcharts.seriesTypes['heatmap'].
// MUST be at module level (not inside init()) so rspack bundles it before any chart call.
// (feedback_highcharts_events_nesting_cast_masks: import * as Highcharts MUST precede the module import)
if (typeof (Highcharts as any).seriesTypes?.['heatmap'] === 'undefined') {
    console.error('[heatmap] module failed to register — /rhythm matrices will not work.');
}

export default class {
    static init = () => {
        renderWeekdayMatrix();
        renderMonthMatrix();
    }
}

// Declared shapes for the two globals injected by Rhythm/default.latte's {block page_js}
// (`rhythm_weekday_matrix` / `rhythm_month_matrix`, confirmed against the shipped template —
// plan 286-06). Both carry the FULL matrix object, not a pre-split shape.
interface RhythmGroupBand {
    group: string;
    from: number;
    to: number;
}

interface RhythmPointCustom {
    rawMean: string | null;
    n: number;
    z: number | null;
}

interface RhythmPoint {
    x: number;
    y: number;
    value: number | null;
    color?: string;
    // Absent entirely for a not-in-this-chart point (never tracked, or — month chart only —
    // excluded from seasonality). Presence/absence of this key, not any field inside it, is
    // what the dataLabel/tooltip formatters below branch on first.
    custom?: RhythmPointCustom;
}

interface RhythmMatrixPayload {
    categories: string[];
    groupBands: RhythmGroupBand[];
    axisLabels: string[];
    colorAxisMin: number;
    colorAxisMax: number;
    points: RhythmPoint[];
}

declare var rhythm_weekday_matrix: RhythmMatrixPayload;
declare var rhythm_month_matrix: RhythmMatrixPayload;

// NOTE: neither chart function below is named after its backing global. src/js/lastfm.ts:64-66
// already recorded why: a same-name function declaration shadows the injected global in module
// scope, so `theGlobal.length` silently becomes the function's arity (0) instead of the
// array/points length, blanking the chart with no error. renderWeekdayMatrix()/
// renderMonthMatrix() read rhythm_weekday_matrix/rhythm_month_matrix as plain, unshadowed
// globals instead.

function renderWeekdayMatrix(): void {
    const el = document.getElementById('rhythm-weekday-matrix');
    if (!el) return;
    if (typeof rhythm_weekday_matrix === 'undefined' || rhythm_weekday_matrix.points.length === 0) return;
    // Sentinel: heatmap module must be registered (mirrors lastfm.ts:121-125's per-chart repeat
    // of the module-top guard above).
    if (typeof (Highcharts as any).seriesTypes?.['heatmap'] === 'undefined') {
        console.error('[renderWeekdayMatrix] heatmap series type not registered — aborting chart init');
        return;
    }
    buildRhythmHeatmap('rhythm-weekday-matrix', rhythm_weekday_matrix, 'Weekday z-score');
}

function renderMonthMatrix(): void {
    const el = document.getElementById('rhythm-month-matrix');
    if (!el) return;
    if (typeof rhythm_month_matrix === 'undefined' || rhythm_month_matrix.points.length === 0) return;
    if (typeof (Highcharts as any).seriesTypes?.['heatmap'] === 'undefined') {
        console.error('[renderMonthMatrix] heatmap series type not registered — aborting chart init');
        return;
    }
    const chart = buildRhythmHeatmap('rhythm-month-matrix', rhythm_month_matrix, 'Month z-score');

    // D-11: this chart lives inside plan 286-06's min-width:560px overflow-x scroller, so
    // Highcharts (which sizes to its container at construction) needs a reflow on resize.
    // Debounced, and guarded by a module-scope flag so a second init() call cannot double-bind
    // the listener — this codebase's own recorded shared-component double-bind failure mode.
    attachMonthChartReflow(chart);
}

/**
 * Shared construction for both /rhythm heatmaps — one diverging colorAxis pinned to the
 * payload's own bound, dataLabels reading point.custom.rawMean, payload-derived group
 * separators, and a neutral nullColor for the payload's explicit not-in-this-chart points.
 * Deltas from lastfm.ts's lastfm_heatmap_matrix() (D-05): sequential colorAxis -> diverging
 * stops; dataLabels off -> on; bare day-name y-axis categories -> registry labels.
 */
function buildRhythmHeatmap(containerId: string, payload: RhythmMatrixPayload, seriesName: string): Highcharts.Chart {
    const categories = payload.categories;
    // D-05/D-02: reads live --ksat-accent at construction, exactly as lastfm.ts:135 does.
    const accentHex = getComputedStyle(document.documentElement).getPropertyValue('--ksat-accent').trim();
    const { plotBands, plotLines } = buildGroupSeparators(payload.groupBands);

    return Highcharts.chart(containerId, Highcharts.merge(getChartTheme(), {
        chart: {
            type: 'heatmap',
            backgroundColor: 'transparent',
            // Component-computed from the row count (matching lastfm.ts's ~37px/row), never a
            // hardcoded pixel value, so a future registry addition needs no manual height edit.
            height: categories.length * 37,
        },
        title: { text: '' },
        xAxis: {
            categories: payload.axisLabels,
            labels: { style: { fontSize: 'var(--ksat-font-size-sm)' } },
        },
        yAxis: {
            categories,
            // Row 0 (the registry's first-declared group/domain) renders at the TOP, matching
            // lastfm.ts's own reversed:true choice and the natural top-to-bottom reading order
            // of Rhythm::orderedDomains()'s group sequence.
            reversed: true,
            title: { text: '' },
            labels: { style: { fontSize: 'var(--ksat-font-size-base)' } },
            plotBands,
            plotLines,
        },
        colorAxis: {
            // D-01/D-10: min/max come from the payload, never hardcoded here and never
            // omitted — omitting them is exactly what triggers Highcharts auto-scale, which
            // would silently re-map colours as the underlying data changes.
            min: payload.colorAxisMin,
            max: payload.colorAxisMax,
            // Diverging, valence-neutral (D-04): cool = below this domain's own average,
            // neutral = average, warm/accent = above — never good/bad.
            stops: [
                [0, 'var(--bs-info)'],
                [0.5, 'var(--ksat-surface-raised)'],
                [1, accentHex],
            ],
        },
        legend: { enabled: false },
        tooltip: {
            // z is a bonus channel here, never printed on the chart itself (D-03).
            formatter: function (this: any): string | boolean {
                const custom = this.point.custom;
                if (!custom) {
                    // Not-in-this-chart point — no figures to show, never a row of undefineds.
                    return false;
                }
                const rawMean = custom.rawMean === null ? 'n=0' : custom.rawMean;
                const z = custom.z === null ? '—' : custom.z.toFixed(2);
                return `${categories[this.point.y]}: ${rawMean} · z=${z} · n=${custom.n}`;
            },
        },
        plotOptions: {
            heatmap: {
                // Styles a Point that already EXISTS with value===null/isNull
                // (node_modules/highcharts/modules/heatmap.src.js:1845-1848, :3205-3209) — this
                // is NOT a grid-fill. Series#generatePoints()
                // (node_modules/highcharts/highcharts.src.js:33090-33117) creates exactly one
                // Point per entry present in series.options.data with no mechanism keyed off
                // axis categories, so an omitted coordinate produces no Point at all and
                // renders as a genuinely blank, undrawn cell. The payload therefore emits an
                // explicit {value:null} point at every coordinate of a not-in-this-chart row —
                // never filter those points out here.
                nullColor: 'var(--ksat-surface-raised)',
            },
        },
        series: [{
            type: 'heatmap',
            name: seriesName,
            data: payload.points,
            dataLabels: {
                enabled: true,
                style: { fontSize: 'var(--ksat-font-size-base)', fontWeight: '600' },
                formatter: function (this: any): string {
                    const custom = this.point.custom;
                    if (!custom) {
                        // custom absent -> not-in-this-chart row (never tracked, or excluded
                        // from seasonality). Render no label at all — never n=0, never 0, never
                        // the strings "null"/"undefined": painting any of those across a
                        // never-tracked row would violate D-13's inherited invariant from the
                        // opposite direction. The row's real reason lives in the provenance
                        // list (D-06), not on the cell.
                        return '';
                    }
                    if (custom.rawMean === null) {
                        // custom present, rawMean null -> an in-chart cell with zero
                        // underlying days (D-15). There is no mean to print; show the count.
                        return 'n=0';
                    }
                    // Every decimal/separator/prefix/suffix rule was already applied
                    // server-side (RhythmPresenter::formatRawMean()) — no client-side number
                    // formatting of any kind here.
                    return custom.rawMean;
                },
            },
        }],
        credits: { enabled: false },
    } as any));
}

/**
 * D-07: group boundaries as yAxis.plotBands (alternating surface-body/surface-card) plus a
 * plotLine at each boundary — derived from the payload's own groupBands array only, never a
 * second hand-typed group order or a second walk of the categories array.
 */
function buildGroupSeparators(groupBands: RhythmGroupBand[]): {
    plotBands: Highcharts.YAxisPlotBandsOptions[];
    plotLines: Highcharts.YAxisPlotLinesOptions[];
} {
    const plotBands: Highcharts.YAxisPlotBandsOptions[] = [];
    const plotLines: Highcharts.YAxisPlotLinesOptions[] = [];

    groupBands.forEach((band, index) => {
        plotBands.push({
            from: band.from - 0.5,
            to: band.to + 0.5,
            color: index % 2 === 0 ? 'var(--ksat-surface-body)' : 'var(--ksat-surface-card)',
        });
        if (index > 0) {
            plotLines.push({
                value: band.from - 0.5,
                color: 'var(--ksat-border)',
                width: 1,
            });
        }
    });

    return { plotBands, plotLines };
}

// Module-scope guard so a second init() call (should one ever occur) cannot double-bind the
// resize listener — this codebase's own recorded shared-component double-bind failure mode.
let monthChartReflowBound = false;

function attachMonthChartReflow(chart: Highcharts.Chart): void {
    if (monthChartReflowBound) return;
    monthChartReflowBound = true;

    let resizeTimer: number | undefined;
    window.addEventListener('resize', () => {
        if (resizeTimer !== undefined) {
            window.clearTimeout(resizeTimer);
        }
        resizeTimer = window.setTimeout(() => {
            chart.reflow();
        }, 150);
    });
}
